import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Briefcase, 
  Users, 
  Target, 
  TrendingUp,
  ChevronDown,
  Building2,
  Layers,
  Settings,
  HeartHandshake
} from 'lucide-react';

const HomePage = () => {
  const stats = [
    { number: '20', suffix: 'ans', label: "d'expérience" },
    { number: '7', suffix: '', label: "domaines d'expertise" },
  ];

  const expertise = [
    { icon: Target, label: 'Management de projet' },
    { icon: Layers, label: 'Transformation digitale' },
    { icon: Building2, label: 'Modèle opérationnel' },
    { icon: HeartHandshake, label: 'Conduite du changement' },
    { icon: Settings, label: 'Déploiement SI' },
    { icon: TrendingUp, label: 'Sélection solutions' },
  ];

  const timeline = [
    {
      year: '2007-2017',
      title: 'Consultant en Management',
      duration: '10 ans',
      company: 'Accenture, Capgemini Invent, PwC',
      description: 'Progression de consultant à manager, spécialisé en conseil en management et intégration de systèmes.',
    },
    {
      year: '2017-2019',
      title: 'Consultant Agile Indépendant',
      duration: '2 ans',
      company: 'Free-lance',
      description: "Pilotage de transformations agiles, construction de modèles opérationnels de gestion produit Scrum, animation d'ateliers d'embarquement.",
    },
    {
      year: '2019-2023',
      title: 'Directeur Organisation Groupe',
      duration: '4 ans',
      company: 'Grande entreprise',
      description: "Constitution et management d'une équipe de 4 collaborateurs, pilotage du programme de transformation groupe et déploiement Salesforce.",
    },
    {
      year: "Aujourd'hui",
      title: 'Directeur de Transformation Indépendant',
      duration: 'Présent',
      company: 'WYCO Management',
      description: "Accompagnement d'entreprises dans leur transformation organisationnelle, pilotage de programmes, déploiement SI et conduite du changement.",
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="hero-section bg-white">
        <div className="container-main relative z-10 text-center pt-20">
          <p className="caption-text text-[#BFA15F] mb-6 animate-fade-in-delay-1">
            Yoann Cohen · Directeur Transformation
          </p>
          <div className="mb-8 animate-fade-in-delay-2">
            <img
              src="https://customer-assets.emergentagent.com/job_cohen-services/artifacts/k2p7p6tm_WYCO%20fond%20blanc%20%281%29.jpg"
              alt="WYCO Management"
              className="h-40 md:h-52 lg:h-64 w-auto mx-auto"
            />
          </div>
          <p className="body-large text-slate-600 max-w-2xl mx-auto mb-12 animate-fade-in-delay-3">
            Triple profil, de Manager en cabinet de conseil de 1er plan, consultant indépendant, 
            puis Directeur Transformation dans une corporate, je suis spécialisé dans la transformation 
            des organisations, le pilotage de programme complexe et transverse et le management d'équipe.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-delay-4">
            <Link to="/services" data-testid="hero-services-btn" className="btn-primary">
              Découvrir mes services
              <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/contact" data-testid="hero-contact-form-btn" className="btn-gold">
              Parlons de vos enjeux
            </Link>
            <a href="mailto:yoann.cohen@wyco.fr" data-testid="hero-contact-btn" className="btn-secondary">
              Me contacter
            </a>
          </div>
          
          {/* Scroll indicator */}
          <div className="scroll-indicator mt-16 text-slate-400">
            <span className="text-xs uppercase tracking-widest mb-2">Découvrir</span>
            <ChevronDown size={20} className="animate-bounce" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-16 bg-white border-b border-slate-100">
        <div className="container-main">
          <div className="grid grid-cols-2 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-number">
                  {stat.number}<span className="text-[#BFA15F]">{stat.suffix}</span>
                </div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section data-testid="about-section" className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="caption-text text-[#BFA15F] mb-4">À propos</p>
              <h2 className="heading-lg mb-6">
                Un triple parcours<br />
                <span className="text-slate-400">unique</span>
              </h2>
              <div className="divider mb-8" />
              <p className="body-large mb-6">
                Un triple parcours entre cabinets de conseil, indépendant et management 
                interne en grande entreprise, qui m'a conféré une vision à 360° sur les 
                dimensions stratégique, opérationnelle et organisationnelle.
              </p>
              <p className="body-text mb-8">
                De consultant junior à Directeur Organisation Groupe, chaque étape a 
                approfondi ma compréhension et ma maîtrise des transformations complexes. 
                Expériences au sein de cabinets de conseil internationaux de premier plan 
                tels qu'Accenture et PwC.
              </p>
              <Link to="/references" data-testid="about-references-btn" className="btn-secondary">
                Voir mes références
                <ArrowRight size={16} className="ml-2" />
              </Link>
            </div>
            
            {/* Expertise Grid */}
            <div className="grid grid-cols-2 gap-4">
              {expertise.map((item, index) => (
                <div 
                  key={index} 
                  className="expertise-badge"
                >
                  <item.icon size={18} className="mr-3 text-[#BFA15F]" />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section data-testid="timeline-section" className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="caption-text text-[#BFA15F] mb-4">Parcours</p>
            <h2 className="heading-lg">Mon parcours professionnel</h2>
          </div>
          
          <div className="max-w-3xl mx-auto timeline-container">
            <div className="timeline-line" />
            {timeline.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-dot" />
                <div className="bg-white p-6 border border-slate-100 hover:border-[#0a1628] hover:shadow-lg transition-all duration-300">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="timeline-year">{item.year}</span>
                    <span className="text-xs text-slate-400">·</span>
                    <span className="text-xs text-slate-500">{item.duration}</span>
                  </div>
                  <h3 className="heading-md text-xl mb-2">{item.title}</h3>
                  <p className="text-sm text-[#BFA15F] font-medium mb-3">{item.company}</p>
                  <p className="body-text">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section data-testid="services-preview-section" className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="caption-text text-[#BFA15F] mb-4">Expertise</p>
            <h2 className="heading-lg mb-6">Services clés</h2>
            <p className="body-large max-w-2xl mx-auto">
              Directeur de Transformation indépendant, j'accompagne les entreprises 
              dans leurs projets de transformation organisationnelle.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Briefcase, title: 'Transformation organisationnelle', desc: 'Accompagnement dans vos projets de transformation' },
              { icon: Target, title: 'Management de projet', desc: 'Pilotage de programmes stratégiques' },
              { icon: Settings, title: 'Déploiement SI', desc: "Mise en œuvre de systèmes d'information" },
              { icon: Users, title: 'Conduite du changement', desc: 'Séminaires et ateliers de direction' },
            ].map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-card-icon">
                  <service.icon size={32} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-3">{service.title}</h3>
                <p className="body-text text-sm">{service.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" data-testid="services-discover-btn" className="btn-primary">
              Tous mes services
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 bg-[#0a1628] text-white">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading mb-6">
            Prêt à transformer votre organisation ?
          </h2>
          <p className="text-slate-300 text-lg max-w-xl mx-auto mb-10">
            Discutons de vos enjeux de transformation et construisons 
            ensemble une trajectoire adaptée à vos objectifs.
          </p>
          <a 
            href="mailto:yoann.cohen@wyco.fr" 
            data-testid="cta-contact-btn"
            className="btn-gold inline-flex items-center"
          >
            Contactez-moi
            <ArrowRight size={16} className="ml-2" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
